import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useReducer, useRef } from "react";
import EnhancedTable from "../../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../../utils/environment";
import { ContactLensConfigurationService } from "../../../../../service/validDataService";
import useAbortController from "../../../../../hooks/useAbortController";
import { DEFAULT_PAGE_SIZE } from "../../../../../utils";
import useDialogModal from "../../../../../hooks/useDialogModal";
import ConfirmationDialog from "../../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "../../../../../utils/validLookup";
import ContactLensMaterialInputForm from "./contactLensMaterialInputForm";
import EditContactLensMaterialForm from "./editContactLensMaterialForm";
import { contactLensMaterialColumns } from "./columns";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopy from "@mui/icons-material/ContentCopy";

export default function ContactLensMaterials() {
    const [ContactLensMaterialInputDialog, showContactLensMaterialInputForm] = useDialogModal(ContactLensMaterialInputForm);
    const [ContactLensMaterialEditDialog, showContactLensMaterialEditForm] = useDialogModal(EditContactLensMaterialForm);

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal] = useAbortController();
    const lastQueryRef = useRef({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showContactLensMaterialInputForm();
    }

    const handleRowEdit = (rowData) => {
        showContactLensMaterialEditForm(null, { recordToEdit: rowData });
    }

    const handleRowCopy = (rowData) => {
        showContactLensMaterialEditForm(null, { recordToEdit: rowData, isCopy: true });
    }

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        ContactLensConfigurationService.getMaterialsWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.rows,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal]);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await ContactLensConfigurationService.deleteMaterial(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.error("Failed to delete material(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete Material',
            message: `Please confirm if you would like to delete ${rowids.length} material(s)?`,
        });
    }

    const recordAddHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    const recordEditHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    return (
        <Box>
            {/* <Typography variant="h4">Contact Lens Materials</Typography> */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Manage contact lens materials with water content and oxygen permeability properties
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <EnhancedTable
                columns={contactLensMaterialColumns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    {
                        icon: <ContentCopy />,
                        onClick: handleRowCopy
                    },
                ]}
            />
            <ContactLensMaterialInputDialog onRecordAdded={recordAddHandler} />
            <ContactLensMaterialEditDialog onRecordEdit={recordEditHandler} />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    );
}
